import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { resolveRichText, stripEdgeEmptyParagraphs, verifyCrossLinks } from '@vibesites/runtime';
import { RichTextContent, EditableToken, useVibesites, useContentToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

// Manually-pasted blurbs (used when hyperlocal copy can't come through the MH
// profile) arrive as HTML that carries its own inline color / font / size and
// default-styled <a> tags, which override the theme's blurb styling. These
// descendant rules re-assert the theme typography (font, color, size, and
// transparent background all forced with `!` to beat inline styles) and give
// every hyperlink a consistent on-brand treatment — so a pasted blurb renders
// exactly like the default field-bound one (e.g. the Pet Policy page) while
// its text and links are preserved as authored.
const BLURB_TEXT_CLASSES =
  'font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] ' +
  '[&_p]:mb-4 [&_p:last-child]:mb-0 ' +
  '[&_*]:!font-[inherit] [&_*]:!text-[color:inherit] [&_*]:![font-size:inherit] [&_*]:!bg-transparent ' +
  '[&_a]:!text-[var(--vibe-color-primary)] [&_a]:underline [&_a]:underline-offset-2 [&_a]:font-medium [&_a]:transition-colors ' +
  '[&_a:hover]:!text-[var(--vibe-color-text)]';

// ── Rich-text render pipeline (mirrors @vibesites/runtime RichTextContent) ──
// A phrasing-only tag can't legally hold block children; the runtime auto-
// promotes to <div> when block content survives, and strips a single wrapping
// <p> so a plain blurb stays a valid <p>. We reproduce that here so the local
// blurb renderer stays hydration-safe while inserting the link-internalizing
// pass below (which the stock RichTextContent doesn't run).
const PHRASING_ONLY_TAGS = new Set(['p', 'span', 'a']);
const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;
const BLOCK_CONTENT_RE = /<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i;

function stripWrappingParagraph(html: string, tag: string): string {
  if (!PHRASING_ONLY_TAGS.has(tag)) return html;
  const cleaned = stripEdgeEmptyParagraphs(html);
  const match = cleaned.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return cleaned || html;
  const inner = match[1];
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(inner)) return cleaned;
  return inner;
}

function collapseBlankParagraphs(html: string): string {
  return html.replace(BLANK_P_RE, '');
}

function hasBlockContent(html: string): boolean {
  return BLOCK_CONTENT_RE.test(html);
}

// Matches a full anchor element; inner content is non-greedy (nested anchors
// aren't valid HTML, so this is sufficient for authored blurb markup).
const ANCHOR_EL_RE = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
// Pulls the href value out of an anchor's attribute string (single/double
// quoted). Leading (?:^|\s) avoids matching a `data-*-href=` tail.
const HREF_RE = /(?:^|\s)href\s*=\s*("([^"]*)"|'([^']*)')/i;
const TARGET_RE = /\s+target\s*=\s*("[^"]*"|'[^']*')/gi;
const REL_RE = /\s+rel\s*=\s*("[^"]*"|'[^']*')/gi;

function normalizeInternalPath(pathname: string): string | null {
  const path = (pathname || '').split(/[?#]/, 1)[0].toLowerCase();
  if (!path.startsWith('/')) return null;
  if (path.startsWith('//')) return null;
  if (path === '/') return '/';
  return path.replace(/\/+$/, '');
}

/**
 * Rewrite absolute/external `<a href>`s that actually point at a page on THIS
 * site into a root-relative internal path, so the link navigates in-site
 * instead of "linking out". Only URLs whose path matches a real page (checked
 * against the site's own `pagePaths`) are internalized — genuinely external
 * links (a partner site, a map, etc.) whose path isn't a page here are left
 * untouched. `target`/`rel` are dropped on internalized links so they open in
 * place. Runs BEFORE verifyCrossLinks, which then validates the now-internal
 * href against the same page set (and applies path synonyms).
 */
function internalizeLinks(html: string, availablePaths?: readonly string[]): string {
  if (!html || !availablePaths || availablePaths.length === 0) return html;
  const pathSet = new Set<string>();
  for (const p of availablePaths) {
    const n = normalizeInternalPath(p);
    if (n) pathSet.add(n);
  }
  if (pathSet.size === 0) return html;

  return html.replace(ANCHOR_EL_RE, (full, attrs: string, inner: string) => {
    const hrefMatch = attrs.match(HREF_RE);
    if (!hrefMatch) return full;
    const href = (hrefMatch[2] ?? hrefMatch[3] ?? '').trim();
    // Only consider absolute (http/https) or protocol-relative URLs — anything
    // already root-relative, or a tel:/mailto:/#fragment, is handled elsewhere.
    if (!/^(https?:)?\/\//i.test(href)) return full;

    let url: URL;
    try {
      url = new URL(href.startsWith('//') ? `https:${href}` : href);
    } catch {
      return full;
    }
    const norm = normalizeInternalPath(url.pathname);
    // Not a path this site serves → treat as a real external link, leave as-is.
    if (!norm || !pathSet.has(norm)) return full;

    const tail = (url.search || '') + (url.hash || '');
    const internal = `${norm}${tail}`;
    const newAttrs = attrs
      .replace(HREF_RE, (m) => m.replace(href, internal))
      .replace(TARGET_RE, '')
      .replace(REL_RE, '');
    return `<a${newAttrs}>${inner}</a>`;
  });
}

/**
 * Local blurb rich-text renderer. Same resolution/hydration handling as the
 * runtime's RichTextContent, plus internalizeLinks() so pasted absolute URLs
 * that reference this site's pages navigate in-site. Still wraps in
 * EditableToken so the blurb stays inline-editable (the editor reads the raw
 * stored token via `path`, not this transformed HTML).
 */
function BlurbRichText({
  path,
  defaultField,
  defaultValue,
  className,
}: {
  path: string;
  defaultField?: string;
  defaultValue?: string;
  className?: string;
}) {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken(path, { defaultField, defaultValue });
  const resolved = rawHtml ? resolveRichText(rawHtml, property as Record<string, any>) : '';
  const internalized = internalizeLinks(resolved, pagePaths);
  const verified = verifyCrossLinks(internalized, pagePaths);
  const html = collapseBlankParagraphs(stripWrappingParagraph(verified, 'p'));
  const effectiveTag = hasBlockContent(html) ? 'div' : 'p';

  return (
    <EditableToken
      path={path}
      type="richtext"
      defaultField={defaultField}
      defaultValue={defaultValue}
      as={effectiveTag}
      className={className}
    >
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: html }}
        style={{ display: 'contents' }}
      />
    </EditableToken>
  );
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const textPath = `${prefix}.text`;

  return (
    <section ref={sectionRef as React.RefObject<HTMLElement>} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <BlurbRichText
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          className={BLURB_TEXT_CLASSES}
        />
      </div>

      {/* Schema-lint anchor for static analysis — hidden at runtime */}
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_long_description"></span>',
      suggestedFields: [
        'property_floorplans_blurb',
        'property_amenities_blurb',
        'property_gallery_blurb',
        'property_neighborhood_blurb',
        'property_virtualtours_blurb',
        'property_contactus_blurb',
        'property_reviews_blurb',
        'property_pets_blurb',
        'property_applynow_blurb',
        'property_residentaccess_blurb',
        'property_scheduletour_blurb',
        'property_interactivemap_blurb',
      ],
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
